import { lazy } from "react";
import { PortfolioGuard, TranslationText } from "components";
import { ProtectedRoute } from "components/ProtectedRoute/ProtectedRoute";
import { TermsOfConditionGuard } from "components/TermsOfConditionGuard/TermsOfConditionGuard";
import { FEATURES } from "config";
import { MainLayout } from "layouts/MainLayout/MainLayout";
import { NavTabRoutes } from "layouts/NavTabLayout/NavTab/NavTabRoutes";
import { NavTabPath } from "layouts/NavTabLayout/NavTab/types";
import { PortfolioNavigationHeaderLayout } from "layouts/PortfolioNavigationHeaderLayout/PortfolioNavigationHeaderLayout";
import { Navigate, Outlet, useRoutes } from "react-router-dom";
import { TAGS_MAP } from "utils/string";
import { NotFoundView } from "views/notFoundView/notFoundView";
import { TermsOfConditionView } from "views/termsOfCondition/termsOfConditionView";
import { authUserMainRoutes } from "../authUser/routes";
import { PortfolioRoutes } from "./portfolio/routes";

const Overview = lazy(() =>
  import("./overview").then((module) => ({ default: module.OverviewPage }))
);
const Holdings = lazy(() =>
  import("./holdings").then((module) => ({ default: module.HoldingsPage }))
);
const Holding = lazy(() =>
  import("./holdings/[holdingId]").then((module) => ({
    default: module.HoldingPage,
  }))
);
const Transactions = lazy(() =>
  import("./transactions").then((module) => ({
    default: module.TransactionsPage,
  }))
);
const TransactionDetails = lazy(() =>
  import("./transactions/[transactionId]").then((module) => ({
    default: module.TransactionDetailsPage,
  }))
);
const SecurityDetails = lazy(() =>
  import("./securities/[securityId]").then((module) => ({
    default: module.SecurityDetailsPage,
  }))
);
const Issuers = lazy(() =>
  import("./issuers").then((module) => ({
    default: module.IssuersPage,
  }))
);
const IssuerDetails = lazy(() =>
  import("./issuers/[issuerId]").then((module) => ({
    default: module.IssuerDetailsPage,
  }))
);
const Orders = lazy(() =>
  import("./orders").then((module) => ({ default: module.OrdersPage }))
);
const OrderDetails = lazy(() =>
  import("./orders/[orderId]").then((module) => ({
    default: module.OrderDetailsPage,
  }))
);
const Documents = lazy(() =>
  import("./documents").then((module) => ({ default: module.DocumentsPage }))
);
const Contact = lazy(() =>
  import("./contact").then((module) => ({ default: module.ContactPage }))
);

// const Trading = lazy(() =>
//   import("./trading").then((module) => ({ default: module.TradingPage }))
// );
const Limits = lazy(() =>
  import("./limits").then((module) => ({ default: module.LimitsPage }))
);
const Reports = lazy(() =>
  import("./reports").then((module) => ({ default: module.ReportsPage }))
);
const Performance = lazy(() =>
  import("./performance").then((module) => ({
    default: module.PerformancePage,
  }))
);
const Bookkeeping = lazy(() =>
  import("./bookkeeping").then((module) => ({
    default: module.BookkeepingsPage,
  }))
);

const Securities = lazy(() =>
  import("./securities").then((module) => ({ default: module.SecuritiesPage }))
);

const Sustainability = lazy(() =>
  import("./sustainability").then((module) => ({
    default: module.SustainabilityPage,
  }))
);

const Webbinar = lazy(() =>
  import("./webbinar").then((module) => ({ default: module.WebbinarPage }))
);

const WebbinarDetail = lazy(() =>
  import("./webbinar/[webbinarId]").then((module) => ({
    default: module.WebbinarDetailPage,
  }))
);

const Exposure = lazy(() =>
  import("./exposure").then((module) => ({ default: module.ExposurePage }))
);

export const mainTabRoutes = (allowedRoutes: string[]): NavTabPath[] => {
  return [
    {
      path: "overview",
      icon: <span className="mgc_home_3_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.overview" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("overview")}>
          <PortfolioGuard>
            <Overview />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "holdings",
      icon: <span className="mgc_chart_pie_2_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.holdings" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("holdings")}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: (
            <PortfolioGuard>
              <Holdings />
            </PortfolioGuard>
          ),
        },
        {
          path: ":holdingId",
          element: <Holding />,
        },
      ],
    },
    {
      path: "transactions",
      icon: <span className="mgc_exchange_euro_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.transactions" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("transactions")}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Transactions />,
        },
        {
          path: ":transactionId",
          element: <TransactionDetails />,
        },
      ],
    },
    {
      path: "orders",
      icon: <span className="mgc_transfer_horizontal_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.orders" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("orders")}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Orders />,
        },
        {
          path: ":orderId",
          element: <OrderDetails />,
        },
      ],
    },
    {
      path: "performance",
      icon: <span className="mgc_trending_up_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.performance" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("performance")}>
          <PortfolioGuard>
            <Performance />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "exposure",
      icon: <span className="mgc_scan_line text-2xl"></span>,
      tabLabel: "Genomlysning",
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("exposure")}>
          <PortfolioGuard>
            <Exposure />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "limits",
      icon: <span className="mgc_balance_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.limits" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("limits")}>
          <PortfolioGuard>
            <Limits />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "reports",
      icon: <span className="mgc_document_2_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.reports" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("reports")}>
          <PortfolioGuard>
            <Reports />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "bookkeeping",
      icon: <span className="mgc_book_2_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.bookkeeping" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("bookkeeping")}>
          <PortfolioGuard>
            <Bookkeeping />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "securities",
      icon: <span className="mgc_list_search_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.securities" />,

      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("securities")}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: (
            <PortfolioGuard>
              <Securities />
            </PortfolioGuard>
          ),
        },
        {
          path: ":securityId",
          element: <SecurityDetails />,
        },
        {
          path: ":securityId/:issuerId",
          element: <IssuerDetails />,
        },
      ],
    },
    {
      path: "issuers",
      icon: <span className="mgc_building_1_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.issuers" />,

      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("issuers")}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: (
            <PortfolioGuard>
              <Issuers />
            </PortfolioGuard>
          ),
        },
        {
          path: ":issuerId",
          element: <IssuerDetails />,
        },
      ],
    },
    // Hållbarhetssidan är dold i Finansportalen – slå på via FEATURES.sustainability.
    // Utan routen omdirigerar NavTabRoutes /sustainability till första tillgängliga flik.
    ...(FEATURES.sustainability
      ? [
          {
            path: "sustainability",
            icon: <span className="mgc_leaf_line text-2xl"></span>,
            tabLabel: (
              <TranslationText translationKey="navTab.tabs.sustainability" />
            ),
            element: (
              <ProtectedRoute
                isAllowed={allowedRoutes.includes("sustainability")}
              >
                <PortfolioGuard>
                  <Sustainability />
                </PortfolioGuard>
              </ProtectedRoute>
            ),
          },
        ]
      : []),
    {
      path: "documents",
      icon: <span className="mgc_pdf_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.documents" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("documents")}>
          <PortfolioGuard>
            <Documents />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "webbinar",
      icon: <span className="mgc_video_line text-2xl"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.webbinar" />,
      element: (
        <ProtectedRoute isAllowed={true}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Webbinar />,
        },
        {
          path: ":webbinarId",
          element: <WebbinarDetail />,
        },
      ],
    },
  ];
};

const contact = {
  path: "contact",
  icon: <span className="mgc_phone_line text-2xl"></span>,
  disabled: false,
  tabLabel: <TranslationText translationKey="navTab.tabs.contact" />,
  element: <Contact />,
};
export const userWithLinkedContactRoutes = (
  routes: NavTabPath[],
  allowedRoutes: string[]
) => [
  {
    path: "/terms-of-condition",
    element: <TermsOfConditionView />,
  },
  {
    path: "",
    element: (
      <TermsOfConditionGuard>
        <MainLayout />
      </TermsOfConditionGuard>
    ),
    children: [
      {
        path: "",
        element: (
          <Navigate
            to={routes.find((route) => !route.disabled)?.path ?? "/contact"}
            replace
          />
        ),
      },
      {
        path: "",
        element: <PortfolioNavigationHeaderLayout />,
        children: [
          {
            path: "*",
            element: <NavTabRoutes routes={routes} />,
          },
        ],
      },
      {
        path: "portfolio/:portfolioId/*",
        element: <PortfolioRoutes allowedRoutes={allowedRoutes} />,
      },
      ...authUserMainRoutes,
      {
        path: "*",
        element: <NotFoundView />,
      },
    ],
  },
];

interface UserWithLinkedContactRoutesProps {
  data: string[];
}
export const UserWithLinkedContactRoutes = ({
  data,
}: UserWithLinkedContactRoutesProps) => {
  const allowedRoutes = data.map((tag) => TAGS_MAP[tag]).filter(Boolean);

  const accessedRoutes = mainTabRoutes(allowedRoutes).map((route) => ({
    ...route,
    disabled: route.disabled ?? !allowedRoutes.includes(route.path),
  }));

  const routes = useRoutes(
    userWithLinkedContactRoutes([...accessedRoutes, contact], allowedRoutes)
  );
  return routes;
};
